// #5910: the relay refuses a list over its per-user cap with `list_capacity` and `retryAfterMs`.
// The list is asked again with backoff, a few times, before the caller sees the error.
export const LIST_RETRY_TYPES: ReadonlySet<string> = new Set([
  "cyborg:list_agents",
  "cyborg:list_archived_sessions",
  "cyborg:list_terminals",
]);
export const MAX_LIST_RETRIES = 4;
const MAX_DELAY_MS = 8_000;
const DEFAULT_RETRY_AFTER_MS = 1_000;

function retryAfter(err: unknown): number | undefined {
  if (!err || typeof err !== "object") return undefined;
  const { code, retryAfterMs } = err as { code?: unknown; retryAfterMs?: unknown }; // cast: an rpc Error's extra fields
  if (code !== "list_capacity") return undefined;
  return typeof retryAfterMs === "number" && retryAfterMs > 0 ? retryAfterMs : DEFAULT_RETRY_AFTER_MS; // prettier-ignore
}

// Any other request is returned untouched: it gets no extra promise tick.
export function withListRetry<T>(
  type: string,
  send: () => Promise<T>,
  wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  random: () => number = Math.random,
): Promise<T> {
  return LIST_RETRY_TYPES.has(type) ? retrying(send, wait, random) : send();
}

async function retrying<T>(
  send: () => Promise<T>,
  wait: (ms: number) => Promise<void>,
  random: () => number,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await send();
    } catch (err) {
      const base = retryAfter(err);
      if (base === undefined || attempt >= MAX_LIST_RETRIES) throw err;
      // intentional: retried below; the last refusal is thrown to the caller
      await wait(Math.min(MAX_DELAY_MS, base * 2 ** attempt) * (1 + random() * 0.5));
    }
  }
}
